import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Broadcast,
  Users,
  FileText,
  ChatsCircle,
  ArrowUpRight,
  CheckCircle,
  Clock,
  PencilSimpleLine,
  XCircle,
  Prohibit,
  ArrowDownLeft,
} from "@phosphor-icons/react";
import { PageHeader, StatusBadge, BillingBadge, formatDate } from "@/components/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";

const statCards = [
  {
    icon: Broadcast,
    label: "Channel",
    key: "total_channels",
    helper: "Nomor bisnis aktif",
    accent: "bg-[#003399] text-white",
    rail: "before:bg-[#003399]",
    testid: "stat-channels",
  },
  {
    icon: Users,
    label: "Kontak",
    key: "total_contacts",
    helper: "Audiens tersimpan",
    accent: "bg-[#0A8A0014] text-[#0A8A00]",
    rail: "before:bg-[#FFDA1A]",
    testid: "stat-contacts",
  },
  {
    icon: FileText,
    label: "Template",
    key: "total_templates",
    helper: "Siap untuk campaign",
    accent: "bg-[#FFDA1A] text-[#111111]",
    rail: "before:bg-[#003399]",
    testid: "stat-templates",
  },
  {
    icon: ChatsCircle,
    label: "Percakapan",
    key: "total_conversations",
    helper: "Interaksi pelanggan",
    accent: "bg-[#F0F2F5] text-[#111111]",
    rail: "before:bg-[#FFDA1A]",
    testid: "stat-conversations",
  },
];

const templateStatuses = [
  { key: "APPROVED", label: "Approved", note: "Siap dipakai", icon: CheckCircle, tone: "text-[#0A8A00]", bg: "bg-[#0A8A0014]" },
  { key: "PENDING", label: "Pending", note: "Menunggu review", icon: Clock, tone: "text-[#E87400]", bg: "bg-[#E8740014]" },
  { key: "DRAFT", label: "Draft", note: "Belum dikirim", icon: PencilSimpleLine, tone: "text-[#484848]", bg: "bg-[#1111110A]" },
  { key: "REJECTED", label: "Rejected", note: "Perlu revisi", icon: XCircle, tone: "text-[#CC0008]", bg: "bg-[#CC000810]" },
  { key: "DISABLED", label: "Disabled", note: "Tidak aktif", icon: Prohibit, tone: "text-[#767676]", bg: "bg-[#1111110A]" },
];

const StatCard = ({ icon: Icon, label, value, helper, accent, rail, testid }) => (
  <div
    className={`relative overflow-hidden bg-white px-6 py-5 pl-7 rounded-[4px] min-h-[150px] flex flex-col justify-between transition-colors hover:bg-[#FCFCFC] before:absolute before:inset-y-0 before:left-0 before:w-1 ${rail}`}
    data-testid={testid}
  >
    <Icon className="absolute -right-2 bottom-2 w-16 h-16 text-[#11111108]" weight="fill" />
    <div className="relative flex items-start justify-between gap-4">
      <span className="text-[11px] tracking-[0.16em] uppercase font-bold text-[#7B8798]">{label}</span>
      <div className={`w-9 h-9 rounded-[4px] flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" weight="bold" />
      </div>
    </div>
    <div className="relative">
      <span className="font-heading text-[34px] font-extrabold text-[#061E67] leading-none">{value}</span>
      <p className="text-sm text-[#7B8798] mt-2">{helper}</p>
    </div>
  </div>
);

const TemplateStatusCard = ({ item, count }) => (
  <div className="bg-white rounded-[4px] p-4 flex items-center gap-4 transition-colors hover:bg-[#FCFCFC]" data-testid={`tpl-status-${item.key}`}>
    <div className={`w-11 h-11 rounded-[4px] flex items-center justify-center ${item.bg}`}>
      <item.icon className={`w-5 h-5 ${item.tone}`} weight="bold" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-3">
        <p className="font-heading text-sm font-bold text-[#111111]">{item.label}</p>
        <span className="font-heading text-2xl font-extrabold text-[#061E67] leading-none">{count}</span>
      </div>
      <p className="text-xs text-[#767676] mt-1">{item.note}</p>
    </div>
  </div>
);

const getMessageName = (message) => message.contact_name || message.to_number || "Kontak tanpa nama";

const getInitials = (value) => {
  const text = (value || "U").trim();
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const getMessagePreview = (message) => {
  const text = message.content?.text;
  if (text) return text;
  return `[${message.message_type || "message"}]`;
};

const DirectionBadge = ({ direction }) => {
  const inbound = direction === "inbound";
  const Icon = inbound ? ArrowDownLeft : ArrowUpRight;

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-[4px] px-2.5 py-1 text-[11px] font-bold ${inbound ? "bg-[#0A8A0014] text-[#0A8A00]" : "bg-[#00339912] text-[#003399]"}`}>
      <Icon className="w-3.5 h-3.5" weight="bold" />
      {inbound ? "Masuk" : "Keluar"}
    </span>
  );
};

const StatCardSkeleton = () => (
  <div className="relative overflow-hidden bg-white px-6 py-5 pl-7 rounded-[4px] min-h-[150px] flex flex-col justify-between before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-[#E8E8E8]">
    <div className="flex items-start justify-between">
      <Skeleton className="h-3 w-20 bg-[#E9EDF3]" />
      <Skeleton className="h-9 w-9 rounded-[4px] bg-[#E9EDF3]" />
    </div>
    <div>
      <Skeleton className="h-9 w-24 bg-[#E9EDF3]" />
      <Skeleton className="h-4 w-32 mt-3 bg-[#E9EDF3]" />
    </div>
  </div>
);

const TemplateStatusSkeleton = () => (
  <div className="bg-white rounded-[4px] p-4 flex items-center gap-4">
    <Skeleton className="h-11 w-11 rounded-[4px] bg-[#E9EDF3]" />
    <div className="flex-1">
      <div className="flex items-center justify-between gap-3">
        <Skeleton className="h-4 w-20 bg-[#E9EDF3]" />
        <Skeleton className="h-7 w-8 bg-[#E9EDF3]" />
      </div>
      <Skeleton className="h-3 w-28 mt-2 bg-[#E9EDF3]" />
    </div>
  </div>
);

const RecentMessageSkeleton = () => (
  <div className="px-6 py-4 flex items-center gap-4">
    <Skeleton className="h-10 w-10 rounded-[4px] bg-[#E9EDF3]" />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-32 bg-[#E9EDF3]" />
        <Skeleton className="h-5 w-14 rounded-[4px] bg-[#E9EDF3]" />
      </div>
      <Skeleton className="h-3.5 w-2/3 mt-3 bg-[#E9EDF3]" />
    </div>
    <div className="hidden sm:flex flex-col items-end gap-2">
      <Skeleton className="h-5 w-16 rounded-[4px] bg-[#E9EDF3]" />
      <Skeleton className="h-3 w-24 bg-[#E9EDF3]" />
    </div>
  </div>
);

const RecentMessageRow = ({ message }) => {
  const name = getMessageName(message);

  return (
    <div className="px-6 py-4 flex items-center gap-4 transition-colors hover:bg-[#FCFCFC]" data-testid={`recent-message-${message.id}`}>
      <div className="w-10 h-10 rounded-[4px] bg-[#003399] text-white flex items-center justify-center text-sm font-heading font-extrabold flex-shrink-0">
        {getInitials(name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-sm font-bold text-[#111111] truncate">{name}</p>
          <DirectionBadge direction={message.direction} />
        </div>
        <p className="text-sm text-[#767676] truncate mt-1">{getMessagePreview(message)}</p>
      </div>
      <div className="hidden md:flex items-center gap-2 flex-shrink-0">
        <BillingBadge billing={message.billing} />
        <StatusBadge status={message.status} />
      </div>
      <div className="text-right flex-shrink-0 min-w-[108px]">
        <p className="text-xs font-semibold text-[#484848]">{formatDate(message.created_at, false)}</p>
        <p className="text-[11px] text-[#767676] mt-1 hidden sm:block">{message.message_type || "message"}</p>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [tplStatus, setTplStatus] = useState({});
  const [loading, setLoading] = useState({
    summary: true,
    recent: true,
    templates: true,
  });
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/summary")
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null))
      .finally(() => setLoading((state) => ({ ...state, summary: false })));
    api.get("/dashboard/recent-messages")
      .then((r) => setRecent(r.data || []))
      .catch(() => setRecent([]))
      .finally(() => setLoading((state) => ({ ...state, recent: false })));
    api.get("/dashboard/template-status-summary")
      .then((r) => setTplStatus(r.data || {}))
      .catch(() => setTplStatus({}))
      .finally(() => setLoading((state) => ({ ...state, templates: false })));
  }, []);

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Ringkasan aktivitas WhatsApp Business Anda." />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
        {loading.summary ? Array.from({ length: 4 }).map((_, index) => (
          <StatCardSkeleton key={index} />
        )) : statCards.map((stat) => (
          <StatCard
            key={stat.key}
            icon={stat.icon}
            label={stat.label}
            value={summary?.[stat.key] ?? "-"}
            helper={stat.helper}
            accent={stat.accent}
            rail={stat.rail}
            testid={stat.testid}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-[4px]">
          <div className="px-6 py-4 border-b border-[#F0F0F0] flex items-center justify-between">
            <div>
              <h3 className="font-heading text-lg font-bold text-[#111111]">Pesan Terbaru</h3>
              <p className="text-sm text-[#767676] mt-1">Aktivitas pesan terbaru dari semua channel.</p>
            </div>
            <button onClick={() => navigate("/messages")} className="text-sm text-[#003399] font-semibold flex items-center gap-1 hover:underline" data-testid="view-all-messages">
              Lihat semua <ArrowUpRight className="w-3.5 h-3.5" weight="bold" />
            </button>
          </div>
          <div className="divide-y divide-[#F5F5F5]">
            {loading.recent && Array.from({ length: 4 }).map((_, index) => <RecentMessageSkeleton key={index} />)}
            {!loading.recent && recent.length === 0 && <p className="px-6 py-10 text-sm text-[#767676] text-center">Belum ada pesan.</p>}
            {!loading.recent && recent.map((message) => <RecentMessageRow key={message.id} message={message} />)}
          </div>
        </div>

        <div className="rounded-[4px]">
          <div className="px-1 pb-4">
            <h3 className="font-heading text-lg font-bold text-[#111111]">Status Template</h3>
            <p className="text-sm text-[#767676] mt-1">Distribusi state template WhatsApp.</p>
          </div>
          <div className="space-y-3">
            {loading.templates ? Array.from({ length: 5 }).map((_, index) => (
              <TemplateStatusSkeleton key={index} />
            )) : templateStatuses.map((item) => (
              <TemplateStatusCard key={item.key} item={item} count={tplStatus[item.key] ?? 0} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
